<script setup lang="ts">
import type { TabsTriggerProps } from 'reka-ui'

import type { HTMLAttributes } from 'vue'
import { cn } from '@elveris/ui/lib/utils'
import { TabsTrigger, useForwardProps } from 'reka-ui'
import { computed } from 'vue'

const props = defineProps<
  TabsTriggerProps & {
    class?: HTMLAttributes['class']
    variant?: 'default' | 'glass' | 'neu' | 'modern' | 'glow'
    size?: 'sm' | 'md' | 'lg'
  }
>()

const delegatedProps = computed(() => {
  const { class: _, variant: __, size: ___, ...delegated } = props

  return delegated
})

const forwardedProps = useForwardProps(delegatedProps)

const triggerClasses = computed(() => {
  const base = [
    // `bg-transparent` resets the browser-native button bg that
    // tailwind-compat preserves (#efefef). Without this, inactive tabs
    // render with light grey fill against the dark surface — invisible.
    'inline-flex items-center justify-center whitespace-nowrap font-medium bg-transparent',
    // `shrink-0`: TabsList scrolls horizontally when its tabs do not fit. Without it the
    // tabs shrank instead (the label's `truncate` lets a flex item go to ~0 width), so a
    // long list never scrolled and each label was cut to a letter ("M.", "C.", "T..").
    'shrink-0',
    'transition-all duration-200 motion-reduce:transition-none',
    'ring-offset-theme-surface-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-brand focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
    'touch-manipulation select-none',
  ]

  const sizeClasses = {
    sm: 'pi-2 py-1 text-xs rounded-md',
    md: 'pi-3 py-1.5 text-sm rounded-md',
    lg: 'pi-4 py-2 text-base rounded-lg',
  }

  const variantClasses = {
    default: {
      base: 'data-[state=active]:bg-theme-surface-1 data-[state=active]:text-theme-fg data-[state=active]:shadow-theme-sm',
      inactive: 'data-[state=inactive]:text-theme-neutral-7 data-[state=inactive]:hover:bg-theme-surface-1/50 data-[state=inactive]:hover:text-theme-fg/80',
    },
    glass: {
      base: 'glass-frosted neu-theme-raised border-theme-neutral-2/30 data-[state=active]:glass-theme-ultra data-[state=active]:neu-theme-pressed data-[state=active]:text-theme-brand data-[state=active]:shadow-modern-md',
      inactive: 'data-[state=inactive]:hover:glass-theme-ultra data-[state=inactive]:hover:border-theme-brand/30',
    },
    neu: {
      base: 'neu-theme-raised border border-theme-neutral-3/50 bg-gradient-to-br from-theme-neutral-1 to-theme-neutral-2 data-[state=active]:neu-theme-pressed data-[state=active]:border-theme-brand/50 data-[state=active]:text-theme-brand',
      inactive: 'data-[state=inactive]:hover-neu-theme-flat data-[state=inactive]:hover:border-theme-brand/30',
    },
    modern: {
      base: 'neu-theme-raised border border-theme-neutral-3/50 bg-gradient-to-br from-theme-neutral-1 to-theme-neutral-2 data-[state=active]:border-theme-brand/40 data-[state=active]:text-theme-brand data-[state=active]:shadow-modern-glow hover-modern-lift',
      inactive: 'data-[state=inactive]:hover-border-theme-brand/30 data-[state=inactive]:hover-modern-lift',
    },
    glow: {
      base: 'neu-theme-raised border border-theme-brand/20 bg-gradient-to-br from-theme-neutral-1 to-theme-neutral-2 data-[state=active]:border-theme-brand/40 data-[state=active]:text-theme-brand data-[state=active]:shadow-modern-glow data-[state=active]:animate-pulse-slow',
      inactive: 'data-[state=inactive]:hover-border-theme-brand/30 data-[state=inactive]:hover-modern-lift data-[state=inactive]:hover:shadow-modern-glow',
    },
  }

  const currentVariant = variantClasses[props.variant || 'default']

  return cn(...base, sizeClasses[props.size || 'md'], currentVariant.base, currentVariant.inactive, props.class)
})
</script>

<template>
  <TabsTrigger v-bind="forwardedProps" :class="triggerClasses">
    <span class="truncate">
      <slot />
    </span>
  </TabsTrigger>
</template>
